import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  collection, addDoc, deleteDoc, doc, updateDoc,
  onSnapshot, query, orderBy, serverTimestamp, setDoc, getDocs
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  ClipboardCopy, Calculator, TrendingUp, CheckSquare, StickyNote,
  Plus, Trash2, X, ChevronUp, ChevronDown, FileText,
  Save, Check, Package, DollarSign, Calendar, AlertCircle,
  Edit3, Settings, Search, Wrench, ArrowRight, Zap, Layers,
  Percent, Briefcase, ChevronRight, ListChecks, Sparkles,
  RefreshCw, Copy, ToggleLeft, ToggleRight, Info
} from 'lucide-react';

// ═══════════════════════════════════════════════
// UTILS
// ═══════════════════════════════════════════════

/** Güvenli sayı dönüşümü — NaN yayılmasını önler */
const safeNum = (val, fallback = 0) => {
  const n = Number(val);
  return isNaN(n) ? fallback : n;
};

const paraCevir = (tutar) => {
  if (tutar == null || tutar === '') return '—';
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency', currency: 'TRY', maximumFractionDigits: 0
  }).format(tutar);
};

const tarihFormatla = (tarihStr) => {
  if (!tarihStr) {
    const b = new Date();
    return `${String(b.getDate()).padStart(2,'0')}.${String(b.getMonth()+1).padStart(2,'0')}.${b.getFullYear()}`;
  }
  const [y, m, d] = tarihStr.split('-');
  return `${d}.${m}.${y}`;
};

const gecerlilikHesapla = (tarihStr) => {
  if (!tarihStr) {
    const g = new Date(); g.setMonth(g.getMonth() + 1);
    return `${String(g.getDate()).padStart(2,'0')}.${String(g.getMonth()+1).padStart(2,'0')}.${g.getFullYear()}`;
  }
  const [y, m, d] = tarihStr.split('-');
  const t = new Date(+y, +m - 1, +d);
  t.setMonth(t.getMonth() + 1);
  return `${String(t.getDate()).padStart(2,'0')}.${String(t.getMonth()+1).padStart(2,'0')}.${t.getFullYear()}`;
};

const uid = () => crypto.randomUUID();

// ═══════════════════════════════════════════════
// SABİTLER
// ═══════════════════════════════════════════════

const VARSAYILAN_NOTLAR = [
  'Fiyatlarımız nakliye hariç olup, nakliye bedeli ayrıca fatura edilecektir.',
  'Ödeme şekli: İş başlangıcında %40 avans, kalanı iş bitiminde peşin.',
  'Fiyatlarımız 15 gün süreyle geçerlidir.',
  'İş programı tarafların mutabakatı ile belirlenecektir.'
];

const SARFIYAT_SABLONLARI = [
  { id: 'tamir_harci', ad: 'Tamir Harcı', birimSarfiyat: 2.5, birim: 'kg', aciklama: 'Çatlak doldurma ve yüzey tamiri' },
  { id: 'xps', ad: 'XPS Levha', birimSarfiyat: 1.05, birim: 'm²', aciklama: 'Isı yalıtım levhası, %5 fire dahil' },
  { id: 'bitum_membran', ad: 'Bitümlü Membran (SBS/APP)', birimSarfiyat: 1.12, birim: 'm²', aciklama: 'Su yalıtımı, %12 bindirme dahil' },
];

const SEKMELER = [
  { id: 'teklif', label: 'Teklif Sihirbazı', kisa: 'Teklif', icon: FileText },
  { id: 'taslak', label: 'Teklif Taslağı',   kisa: 'Taslak',  icon: Sparkles },
  { id: 'hesap',  label: 'Sarfiyat',          kisa: 'Sarfiyat',icon: Package },
  { id: 'fiyat',  label: 'Fiyat Takibi',      kisa: 'Fiyat',   icon: TrendingUp },
  { id: 'gorev',  label: 'Görevler & Notlar', kisa: 'Görev',   icon: CheckSquare },
];

// ═══════════════════════════════════════════════
// KÜÇÜK UI YARDIMCILARI
// ═══════════════════════════════════════════════

const Etiket = ({ children }) => (
  <span className="text-[9px] font-black uppercase tracking-widest text-stone-500 block mb-1.5">{children}</span>
);

const Alan = ({ className = '', ...props }) => (
  <input
    className={`bg-stone-950 border border-stone-800 rounded-xl px-4 py-3 text-sm text-white outline-none
      focus:border-stone-500 placeholder:text-stone-700 transition-colors w-full ${className}`}
    {...props}
  />
);

const Secim = ({ children, className = '', ...props }) => (
  <select
    className={`bg-stone-950 border border-stone-800 rounded-xl px-4 py-3 text-sm text-white outline-none
      focus:border-stone-500 transition-colors w-full ${className}`}
    {...props}
  >
    {children}
  </select>
);

const Btn = ({ variant = 'primary', size = 'md', className = '', children, ...props }) => {
  const variants = {
    primary: 'bg-white text-stone-900 hover:bg-stone-200',
    ghost:   'bg-stone-900 border border-stone-800 text-stone-300 hover:border-stone-600 hover:text-white',
    danger:  'bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20',
    success: 'bg-green-500/10 border border-green-500/20 text-green-400 hover:bg-green-500/20',
  };
  const sizes = {
    sm: 'px-3 py-2 text-[10px] rounded-lg',
    md: 'px-5 py-3 text-xs rounded-xl',
    lg: 'px-6 py-4 text-sm rounded-2xl',
  };
  return (
    <button
      className={`font-black uppercase tracking-wider transition-all active:scale-[0.97] flex items-center gap-2
        disabled:opacity-40 disabled:cursor-not-allowed ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const KartBaslik = ({ icon: Icon, children, right }) => (
  <div className="flex items-center justify-between mb-5">
    <div className="flex items-center gap-2">
      {Icon && <Icon size={14} className="text-stone-500" />}
      <h3 className="text-[10px] font-black uppercase tracking-widest text-stone-400">{children}</h3>
    </div>
    {right}
  </div>
);

// ═══════════════════════════════════════════════
// MODÜL: TEKLİF HTML ÜRETICI
// ═══════════════════════════════════════════════

const teklifHTMLUret = ({ teklifTarihi, gecerlilik, isTanimi, kapsamMetinleri, fiyatSatirlari, notlar }) => {
  const genelToplam = fiyatSatirlari.reduce((a, s) => a + safeNum(s.toplam), 0);

  const fiyatRows = fiyatSatirlari.filter(s => s.tanim.trim()).map((s, i) => `
    <tr>
      <td style="border-right:0pt solid #000;padding:5.5pt 7pt;border-bottom:1pt solid #e8e8e8;vertical-align:middle;background:#fff;width:23pt;text-align:center;">
        <span style="font-family:Arial;font-size:8.5pt;color:#6b6b6b;">${i + 1}</span>
      </td>
      <td style="padding:5.5pt 7pt;border-bottom:1pt solid #e8e8e8;vertical-align:middle;background:#fff;">
        <span style="font-family:Arial;font-size:10pt;color:#6b6b6b;">${s.tanim}</span>
      </td>
      <td style="padding:5.5pt 7pt;border-bottom:1pt solid #e8e8e8;vertical-align:middle;background:#fff;width:50pt;text-align:center;">
        <span style="font-family:Arial;font-size:10pt;">${s.miktar || ''}</span>
      </td>
      <td style="padding:5.5pt 7pt;border-bottom:1pt solid #e8e8e8;vertical-align:middle;background:#fff;width:45pt;text-align:center;">
        <span style="font-family:Arial;font-size:10pt;">${s.birim || ''}</span>
      </td>
      <td style="padding:5.5pt 7pt;border-bottom:1pt solid #e8e8e8;vertical-align:middle;background:#fff;width:63.5pt;text-align:right;">
        <span style="font-family:Arial;font-size:10pt;">${s.birimFiyat ? paraCevir(s.birimFiyat) : ''}</span>
      </td>
      <td style="padding:5.5pt 7pt;border-bottom:1pt solid #e8e8e8;vertical-align:middle;background:#fff;width:73.5pt;text-align:right;">
        <span style="font-family:Arial;font-size:9.5pt;font-weight:700;">${s.toplam ? paraCevir(s.toplam) : ''}</span>
      </td>
    </tr>
  `).join('');

  const kapsamList = kapsamMetinleri.map(m =>
    `<p style="margin:0;padding:4pt 0;font-family:Arial;font-size:9pt;color:#6b6b6b;">&mdash; ${m}</p>`
  ).join('');

  // FİX: notlar.split('\n') — \\n değil
  const notlarList = notlar.split('\n').filter(n => n.trim()).map(n =>
    `<p style="margin:0;padding:3.5pt 0;font-family:Arial;font-size:8pt;color:#6b6b6b;">&mdash; ${n.trim()}</p>`
  ).join('');

  return `<html><head><meta content="text/html;charset=UTF-8" http-equiv="content-type"></head>
<body style="background:#fff;max-width:523.3pt;margin:0 auto;padding:0;">
<table cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;border:none;">
  <tr><td bgcolor="#1e1e1e" style="padding:11pt 12pt;width:192pt;border:none;">
    <p style="margin:0;"><span style="font-family:Arial;font-size:28pt;font-weight:700;color:#fff;">AGE</span></p>
    <p style="margin:2pt 0 0;"><span style="font-family:Arial;font-size:5.5pt;color:#888;font-weight:700;letter-spacing:.5pt;">&nbsp;İZOLASYON VE ÇATI SİSTEMLERİ</span></p>
  </td>
  <td bgcolor="#1e1e1e" style="padding:11pt 12pt;width:192pt;border:none;"></td>
  <td bgcolor="#1e1e1e" style="padding:11pt 12pt;vertical-align:bottom;width:139pt;border:none;">
    <p style="margin:0;text-align:right;"><span style="font-family:Arial;font-size:6.5pt;color:#888;font-weight:700;letter-spacing:.5pt;">TEKLİF FORMU&nbsp;</span></p>
  </td></tr>
</table>
<p style="margin:0;height:10pt;">&nbsp;</p>
<table cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;border:none;">
  <tr><td bgcolor="#f5f5f5" style="padding:8pt 11pt;width:50%;border:none;">
    <p style="margin:0;"><span style="font-family:Arial;font-size:6.5pt;color:#adadad;">DÜZENLENME TARİHİ</span></p>
    <p style="margin:3pt 0 0;"><span style="font-family:Arial;font-size:10pt;font-weight:700;color:#1e1e1e;">${tarihFormatla(teklifTarihi)}</span></p>
  </td>
  <td bgcolor="#f5f5f5" style="padding:8pt 11pt;width:50%;text-align:right;border:none;">
    <p style="margin:0;"><span style="font-family:Arial;font-size:6.5pt;color:#adadad;">GEÇERLİLİK SÜRESİ</span></p>
    <p style="margin:3pt 0 0;"><span style="font-family:Arial;font-size:10pt;font-weight:700;color:#1e1e1e;">${gecerlilik}</span></p>
  </td></tr>
</table>
<p style="margin:0;height:10pt;">&nbsp;</p>
<table cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;border:none;">
  <tr><td style="border-left:1pt solid #1e1e1e;padding:7pt 12pt;width:100%;border-top:none;border-bottom:none;border-right:none;">
    <p style="margin:0;"><span style="font-family:Arial;font-size:6.5pt;color:#adadad;">İŞ TANIMI</span></p>
    <p style="margin:3pt 0 0;"><span style="font-family:Arial;font-size:10pt;font-weight:700;color:#1e1e1e;text-transform:uppercase;">${isTanimi || 'BELİRTİLMEMİŞ'}</span></p>
  </td></tr>
</table>
<p style="margin:14pt 0 4pt;font-family:Arial;font-size:6.5pt;color:#adadad;">KAPSAM</p>
<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border-top:1px solid #e8e8e8;border-bottom:none;border-left:none;border-right:none;">
  <tr><td style="border:none;padding-top:4pt;">${kapsamList || '<p style="margin:0;font-family:Arial;font-size:9pt;color:#adadad;">Kapsam maddesi eklenmemiş</p>'}</td></tr>
</table>
<p style="margin:0;height:9pt;">&nbsp;</p>
<p style="margin:4pt 0;font-family:Arial;font-size:6.5pt;color:#adadad;">FİYAT TABLOSU</p>
<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:none;">
  <tr>
    <td bgcolor="#1e1e1e" style="padding:6pt 7pt;width:23pt;text-align:center;border:none;"><span style="font-family:Arial;font-size:7.5pt;color:#fff;font-weight:700;">#</span></td>
    <td bgcolor="#1e1e1e" style="padding:6pt 7pt;border:none;"><span style="font-family:Arial;font-size:7.5pt;color:#fff;font-weight:700;">TANIM</span></td>
    <td bgcolor="#1e1e1e" style="padding:6pt 7pt;width:50pt;border:none;"></td>
    <td bgcolor="#1e1e1e" style="padding:6pt 7pt;width:45pt;border:none;"></td>
    <td bgcolor="#1e1e1e" style="padding:6pt 7pt;width:63.5pt;border:none;"></td>
    <td bgcolor="#1e1e1e" style="padding:6pt 7pt;width:73.5pt;text-align:right;border:none;"><span style="font-family:Arial;font-size:7.5pt;color:#fff;font-weight:700;">TOPLAM</span></td>
  </tr>
  ${fiyatRows}
  <tr>
    <td bgcolor="#1e1e1e" colspan="5" style="background:#1e1e1e;padding:8pt 7pt;border:none;">
      <span style="font-family:Arial;font-size:8.5pt;color:#fff;font-weight:700;">GENEL TOPLAM</span>
    </td>
    <td bgcolor="#1e1e1e" style="background:#1e1e1e;padding:8pt 7pt;text-align:right;border:none;">
      <span style="font-family:Arial;font-size:12pt;font-weight:700;color:#fff;">${paraCevir(genelToplam)}</span>
    </td>
  </tr>
</table>
<p style="margin:18pt 0 4pt;font-family:Arial;font-size:6.5pt;color:#adadad;">NOTLAR &amp; ŞARTLAR</p>
<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border-top:1px solid #e8e8e8;border-bottom:none;border-left:none;border-right:none;">
  <tr><td style="border:none;padding-top:4pt;">${notlarList}</td></tr>
</table>
<p style="margin:0;height:10pt;">&nbsp;</p>
<table cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;margin:10pt 0 0;border:none;">
  <tr>
    <td style="padding:8pt 11pt 10pt;border-top:1pt solid #1e1e1e;width:50%;border-bottom:none;border-left:none;border-right:none;">
      <p style="margin:0;"><span style="font-family:Arial;font-size:9.5pt;font-weight:700;color:#1e1e1e;">AGE İzolasyon ve Çatı Sistemleri</span></p>
      <p style="margin:3pt 0 0;"><span style="font-family:Arial;font-size:8.5pt;color:#6b6b6b;">İbrahim Öztin</span></p>
      <p style="margin:1.5pt 0 0;"><span style="font-family:Arial;font-size:8pt;color:#adadad;">+90 (507) 855 52 15</span></p>
    </td>
    <td style="padding:8pt 11pt 10pt;border-top:1pt solid #1e1e1e;width:50%;text-align:right;border-bottom:none;border-left:none;border-right:none;">
      <p style="margin:0;"><span style="font-family:Arial;font-size:6.5pt;color:#adadad;">İmza / Kaşe</span></p>
    </td>
  </tr>
</table>
<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border-top:1px solid #e8e8e8;border-bottom:none;border-left:none;border-right:none;">
  <tr><td style="border:none;padding-top:5pt;">
    <p style="margin:0;font-family:Arial;font-size:6.5pt;color:#adadad;">AGE İzolasyon ve Çatı Sistemleri &nbsp;&middot;&nbsp; <a href="http://ageizolasyon.com" style="color:inherit;text-decoration:none;font-size:7.5pt;">ageizolasyon.com</a> &nbsp;&middot;&nbsp; Sancaktepe / İstanbul &nbsp;+90 (507) 855 52 15</p>
  </td></tr>
</table>
</body></html>`;
};

// ═══════════════════════════════════════════════
// MODÜL: TEKLİF SİHİRBAZI HESAPLAMA
// ═══════════════════════════════════════════════

/** FİX: sonFiyatlar (malzemeGecmisi'nden) + iscilikFiyatlari kullanır; ttMalzemeKutuphanesi KALDIRILDI */
const hesaplaIsKalemi = (kalem, sonFiyatlar, iscilikFiyatlari, varsayilanIsci, varsayilanYevmiye, varsayilanKar) => {
  // Malzeme — sonFiyatlar'dan (Fiyat Takibi'nden çekilen son fiyatlar)
  const malzeme = sonFiyatlar.find(m => m.id === kalem.malzemeId);
  if (!malzeme) return { ...kalem, hata: 'Malzeme bulunamadı (Fiyat Takibi\'nden seçin)' };

  const alan       = safeNum(kalem.alanM2);
  const sarfiyat   = safeNum(malzeme.sarfiyat, 1);
  const birimFiyat = safeNum(malzeme.fiyat);           // sonFiyatlar'da alan adı: fiyat
  const malzemeMaliyeti = alan * sarfiyat * birimFiyat;

  // İşçilik — FİX: iscilikFiyatlari'nden m2BirimFiyati kullan
  const iscilik = iscilikFiyatlari.find(i => i.id === kalem.iscilikId);
  let iscilikMaliyeti = 0;

  if (iscilik) {
    // İşçilik birim fiyatı (₺/m²) × alan
    iscilikMaliyeti = safeNum(iscilik.m2BirimFiyati) * alan;
  } else {
    // Fallback: yevmiye bazlı hesap
    const isciSayisi       = safeNum(kalem.isciSayisi, varsayilanIsci);
    const iscilikSuresi100 = safeNum(kalem.iscilikSuresi100m2, 1);
    const iscilikGunu      = (alan / 100) * iscilikSuresi100;
    iscilikMaliyeti        = iscilikGunu * safeNum(varsayilanYevmiye) * isciSayisi;
  }

  const karOrani   = kalem.ozelKarOrani !== '' && kalem.ozelKarOrani != null
    ? safeNum(kalem.ozelKarOrani)
    : safeNum(varsayilanKar);

  const araToplam    = malzemeMaliyeti + iscilikMaliyeti;
  const toplamKali   = araToplam * (1 + karOrani / 100);
  const birimFiyatKali = alan > 0 ? toplamKali / alan : 0;

  return {
    ...kalem,
    malzemeAdi: malzeme.malzemeAdi,
    malzemeMaliyeti,
    iscilikMaliyeti,
    araToplam,
    karOrani,
    toplamKali,
    birimFiyatKali,
    hata: null
  };
};

// ═══════════════════════════════════════════════
// ANA BİLEŞEN
// ═══════════════════════════════════════════════

export default function Araclar() {
  const [aktifSekme,    setAktifSekme]    = useState('teklif');
  const [onizlemeModu,  setOnizlemeModu]  = useState(false);
  const [toast,         setToast]         = useState(null);

  // ─── MODÜL 1: TEKLİF SİHİRBAZI ───
  const bugun = new Date().toISOString().split('T')[0];
  const [teklifTarihi,    setTeklifTarihi]    = useState(bugun);
  const [gecerlilik,      setGecerlilik]      = useState(() => gecerlilikHesapla(bugun));
  const [isTanimi,        setIsTanimi]        = useState('');
  const [kapsamKutuphanesi, setKapsamKutuphanesi] = useState([]);
  const [seciliKapsam,    setSeciliKapsam]    = useState([]);   // id[]
  const [manuelKapsam,    setManuelKapsam]    = useState([]);   // {id,metin}[]
  const [kapsamArama,     setKapsamArama]     = useState('');
  const aramaRef = useRef(null);

  const [fiyatSatirlari,  setFiyatSatirlari]  = useState([{
    id: uid(), tanim: '', miktar: '1', birim: 'm²', birimFiyat: '', toplam: '', toplamModu: false
  }]);
  const [notlar,          setNotlar]          = useState('');
  const [kopyaDurum,      setKopyaDurum]      = useState('');

  // Kapsam kütüphane modal
  const [kutuphaneModal,       setKutuphaneModal]       = useState(false);
  const [yeniKapsamMetin,      setYeniKapsamMetin]      = useState('');
  const [duzenleKapsamId,      setDuzenleKapsamId]      = useState(null);
  const [duzenleKapsamMetin,   setDuzenleKapsamMetin]   = useState('');

  // ─── MODÜL 2: SARFİYAT ───
  const [m2Alani,              setM2Alani]              = useState('');
  const [serbestAd,            setSerbestAd]            = useState('');
  const [serbestM2,            setSerbestM2]            = useState('');
  const [serbestOran,          setSerbestOran]          = useState('');
  const [serbestBirim,         setSerbestBirim]         = useState('kg');

  // ─── MODÜL 3: FİYAT TAKİBİ ───
  const [malzemeGecmisi,       setMalzemeGecmisi]       = useState([]);
  const [iscilikFiyatlari,     setIscilikFiyatlari]     = useState([]);
  const [malzemeForm,          setMalzemeForm]          = useState({ ad: '', fiyat: '', birim: 'm²', sarfiyat: '1' });
  const [iscilikForm,          setIscilikForm]          = useState({ isKalemi: '', m2BirimFiyati: '' });
  const [duzenleMalzemeId,     setDuzenleMalzemeId]     = useState(null);
  const [duzenleIscilikId,     setDuzenleIscilikId]     = useState(null);

  // ─── MODÜL 4: GÖREVLER & NOTLAR ───
  const [gorevler,             setGorevler]             = useState([]);
  const [gorevBaslik,          setGorevBaslik]          = useState('');
  const [hizliNot,             setHizliNot]             = useState('');

  // ─── MODÜL 5: TEKLİF TASLAĞI ───
  const [ttVarsayilanIsci,     setTtVarsayilanIsci]     = useState(4);
  const [ttVarsayilanYevmiye,  setTtVarsayilanYevmiye]  = useState(5000);
  const [ttVarsayilanKar,      setTtVarsayilanKar]      = useState(15);
  const [ttIsKalemleri,        setTtIsKalemleri]        = useState([]);
  const [ttAltSekme,           setTtAltSekme]           = useState('kalemler');

  // FİX: ttYeniIsKalemi — tüm alanlar dahil (iscilikId, iscilikSuresi100m2)
  const BOSH_IS_KALEMI = {
    ad: '', kapsamMetni: '', malzemeId: '', iscilikId: '',
    alanM2: '', isciSayisi: '', ozelKarOrani: '',
    iscilikSuresi100m2: '1', iscilikSuresiBirim: 'gun'
  };
  const [ttYeniIsKalemi,       setTtYeniIsKalemi]       = useState(BOSH_IS_KALEMI);
  const [ttDuzenleIsKalemiId,  setTtDuzenleIsKalemiId]  = useState(null);

  // ─── TOAST ───
  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  }, []);

  // ─── LOCALSTORAGE ───
  useEffect(() => {
    const k = localStorage.getItem('age_teklif_notlar');
    setNotlar(k || VARSAYILAN_NOTLAR.join('\n'));
  }, []);
  useEffect(() => {
    if (notlar) localStorage.setItem('age_teklif_notlar', notlar);
  }, [notlar]);

  // Tarih değişince geçerlilik otomatik
  useEffect(() => { setGecerlilik(gecerlilikHesapla(teklifTarihi)); }, [teklifTarihi]);

  // ─── FİREBASE — LAZY LOADING (sadece aktif sekme) ───
  useEffect(() => {
    // Teklif sihirbazı her zaman kapsam kütüphanesine ihtiyaç duyar
    const qKapsam = query(collection(db, 'kapsamKutuphanesi'), orderBy('siralama'));
    const u1 = onSnapshot(qKapsam, s => setKapsamKutuphanesi(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => u1();
  }, []);

  useEffect(() => {
    if (aktifSekme !== 'fiyat' && aktifSekme !== 'taslak' && aktifSekme !== 'teklif') return;
    const qM = query(collection(db, 'malzemeFiyatTarihcesi'), orderBy('tarih', 'desc'));
    const u2 = onSnapshot(qM, s => setMalzemeGecmisi(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const qI = query(collection(db, 'iscilikFiyatlari'), orderBy('guncellemeTarihi', 'desc'));
    const u3 = onSnapshot(qI, s => setIscilikFiyatlari(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => { u2(); u3(); };
  }, [aktifSekme]);

  useEffect(() => {
    if (aktifSekme !== 'gorev') return;
    const qG = query(collection(db, 'araclarGorevler'), orderBy('olusturmaTarihi', 'desc'));
    const u4 = onSnapshot(qG, s => setGorevler(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u5 = onSnapshot(doc(db, 'araclarNotlar', 'hizliNot'), s => {
      if (s.exists()) setHizliNot(s.data().icerik || '');
    });
    return () => { u4(); u5(); };
  }, [aktifSekme]);

  useEffect(() => {
    if (aktifSekme !== 'taslak') return;
    // FİX: ttMalzemeKutuphanesi KALDIRILDI — sonFiyatlar'dan çekilecek
    const qK = query(collection(db, 'ttIsKalemleri'), orderBy('olusturmaTarihi', 'desc'));
    const u6 = onSnapshot(qK, s => setTtIsKalemleri(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => u6();
  }, [aktifSekme]);

  // ─── HESAPLAMALAR ───

  const genelToplam = useMemo(() =>
    fiyatSatirlari.reduce((a, s) => a + safeNum(s.toplam), 0),
  [fiyatSatirlari]);

  // FİX: Her malzeme için en güncel kaydı seç
  const sonFiyatlar = useMemo(() => {
    const map = {};
    malzemeGecmisi.forEach(m => {
      const mevcut = map[m.malzemeAdi];
      const mevTarih = mevcut?.tarih?.toDate?.() ?? new Date(0);
      const yeniTarih = m.tarih?.toDate?.() ?? new Date(0);
      if (!mevcut || yeniTarih > mevTarih) map[m.malzemeAdi] = m;
    });
    return Object.values(map);
  }, [malzemeGecmisi]);

  const sarfiyatSonuclari = useMemo(() => {
    const m2 = safeNum(m2Alani);
    const sonuclar = m2 > 0
      ? SARFIYAT_SABLONLARI.map(s => ({
          urun: s.ad, miktar: (m2 * s.birimSarfiyat).toFixed(2),
          birim: s.birim, aciklama: s.aciklama
        }))
      : [];
    if (safeNum(serbestM2) > 0 && safeNum(serbestOran) > 0 && serbestAd.trim()) {
      sonuclar.push({
        urun: serbestAd,
        miktar: (safeNum(serbestM2) * safeNum(serbestOran)).toFixed(2),
        birim: serbestBirim,
        aciklama: `Serbest hesap: ${serbestOran} ${serbestBirim}/m²`
      });
    }
    return sonuclar;
  }, [m2Alani, serbestAd, serbestM2, serbestOran, serbestBirim]);

  // FİX: ttHesaplanmisKalemler artık sonFiyatlar + iscilikFiyatlari kullanıyor
  const ttHesaplanmisKalemler = useMemo(() =>
    ttIsKalemleri.map(k =>
      hesaplaIsKalemi(k, sonFiyatlar, iscilikFiyatlari,
        ttVarsayilanIsci, ttVarsayilanYevmiye, ttVarsayilanKar)
    ),
  [ttIsKalemleri, sonFiyatlar, iscilikFiyatlari, ttVarsayilanIsci, ttVarsayilanYevmiye, ttVarsayilanKar]);

  const ttGenelToplam = useMemo(() =>
    ttHesaplanmisKalemler.reduce((a, k) => a + safeNum(k.toplamKali), 0),
  [ttHesaplanmisKalemler]);

  const filtreliKapsam = useMemo(() => {
    if (!kapsamArama.trim()) return [];
    const q = kapsamArama.toLowerCase();
    return kapsamKutuphanesi
      .filter(k => k.metin.toLowerCase().includes(q) && !seciliKapsam.includes(k.id))
      .slice(0, 5);
  }, [kapsamArama, kapsamKutuphanesi, seciliKapsam]);

  // ─── TEKLİF FONKSİYONLARI ───

  const kapsamToggle = (id) => {
    setSeciliKapsam(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
    setKapsamArama('');
  };

  const kapsamSiraYukari = (idx) => {
    setSeciliKapsam(p => { const a = [...p]; [a[idx], a[idx-1]] = [a[idx-1], a[idx]]; return a; });
  };
  const kapsamSiraAsagi = (idx) => {
    setSeciliKapsam(p => { const a = [...p]; [a[idx], a[idx+1]] = [a[idx+1], a[idx]]; return a; });
  };

  const manuelKapsamEkle  = () => setManuelKapsam(p => [...p, { id: uid(), metin: '' }]);
  const manuelKapsamGuncelle = (id, metin) => setManuelKapsam(p => p.map(k => k.id === id ? { ...k, metin } : k));
  const manuelKapsamSil   = (id) => setManuelKapsam(p => p.filter(k => k.id !== id));

  const fiyatSatirEkle    = () => setFiyatSatirlari(p => [...p, {
    id: uid(), tanim: '', miktar: '1', birim: 'm²', birimFiyat: '', toplam: '', toplamModu: false
  }]);

  const fiyatSatirGuncelle = (id, alan, deger) => {
    setFiyatSatirlari(p => p.map(s => {
      if (s.id !== id) return s;
      const y = { ...s, [alan]: deger };
      if (!s.toplamModu && (alan === 'miktar' || alan === 'birimFiyat'))
        y.toplam = String(safeNum(y.miktar) * safeNum(y.birimFiyat));
      return y;
    }));
  };

  const toplamModuToggle = (id) => setFiyatSatirlari(p => p.map(s =>
    s.id === id ? { ...s, toplamModu: !s.toplamModu, birimFiyat: '', toplam: '' } : s
  ));

  const fiyatSatirSil = (id) => {
    if (fiyatSatirlari.length <= 1) return;
    setFiyatSatirlari(p => p.filter(s => s.id !== id));
  };

  const teklifiKopyala = async () => {
    const seciliMetinler = seciliKapsam
      .map(id => kapsamKutuphanesi.find(x => x.id === id)?.metin)
      .filter(Boolean);
    const kapsamMetinleri = [...seciliMetinler, ...manuelKapsam.map(k => k.metin)].filter(Boolean);

    const html = teklifHTMLUret({ teklifTarihi, gecerlilik, isTanimi, kapsamMetinleri, fiyatSatirlari, notlar });
    const blob = new Blob([html], { type: 'text/html' });
    const plain = `AGE İZOLASYON - TEKLİF\n\nTarih: ${tarihFormatla(teklifTarihi)}\nGeçerlilik: ${gecerlilik}\n\nİŞ: ${isTanimi}\n\nKAPSAM:\n${kapsamMetinleri.map((m,i)=>`${i+1}. ${m}`).join('\n')}\n\nFİYAT:\n${fiyatSatirlari.filter(s=>s.tanim.trim()).map((s,i)=>`${i+1}. ${s.tanim} ${s.miktar}${s.birim} = ${paraCevir(s.toplam)}`).join('\n')}\n\nTOPLAM: ${paraCevir(genelToplam)}`;

    try {
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': blob, 'text/plain': new Blob([plain], { type: 'text/plain' }) })
      ]);
      setKopyaDurum('ok');
      setTimeout(() => setKopyaDurum(''), 2000);
      showToast('Teklif panoya kopyalandı!');
    } catch {
      try {
        await navigator.clipboard.writeText(plain);
        showToast('Metin olarak kopyalandı');
      } catch {
        showToast('Kopyalama başarısız', 'error');
      }
    }
  };

  // Kapsam kütüphanesi CRUD
  const kapsamKutuphanesineEkle = async () => {
    if (!yeniKapsamMetin.trim()) return;
    const snap = await getDocs(query(collection(db, 'kapsamKutuphanesi'), orderBy('siralama', 'desc')));
    const maxSira = snap.docs[0]?.data().siralama || 0;
    await addDoc(collection(db, 'kapsamKutuphanesi'), { metin: yeniKapsamMetin.trim(), siralama: maxSira + 1 });
    setYeniKapsamMetin('');
    showToast('Kapsam eklendi');
  };

  const kapsamKutuphanesindenSil = async (id) => {
    await deleteDoc(doc(db, 'kapsamKutuphanesi', id));
    setSeciliKapsam(p => p.filter(x => x !== id));
    showToast('Kapsam silindi');
  };

  const kapsamDuzenleKaydet = async () => {
    if (!duzenleKapsamMetin.trim()) return;
    await updateDoc(doc(db, 'kapsamKutuphanesi', duzenleKapsamId), { metin: duzenleKapsamMetin.trim() });
    setDuzenleKapsamId(null);
    showToast('Güncellendi');
  };

  // Malzeme fiyat takibi CRUD
  const malzemeEkle = async () => {
    if (!malzemeForm.ad.trim() || !malzemeForm.fiyat) return;
    const data = {
      malzemeAdi: malzemeForm.ad.trim(),
      fiyat: safeNum(malzemeForm.fiyat),
      birim: malzemeForm.birim,
      sarfiyat: safeNum(malzemeForm.sarfiyat, 1),
      tarih: serverTimestamp()
    };
    if (duzenleMalzemeId) {
      await updateDoc(doc(db, 'malzemeFiyatTarihcesi', duzenleMalzemeId), data);
      setDuzenleMalzemeId(null);
      showToast('Malzeme güncellendi');
    } else {
      await addDoc(collection(db, 'malzemeFiyatTarihcesi'), data);
      showToast('Malzeme eklendi');
    }
    setMalzemeForm({ ad: '', fiyat: '', birim: 'm²', sarfiyat: '1' });
  };

  const malzemeDuzenleAc = (m) => {
    setDuzenleMalzemeId(m.id);
    setMalzemeForm({ ad: m.malzemeAdi, fiyat: String(m.fiyat), birim: m.birim, sarfiyat: String(m.sarfiyat || 1) });
  };

  const malzemeSil = async (id) => {
    await deleteDoc(doc(db, 'malzemeFiyatTarihcesi', id));
    showToast('Malzeme silindi');
  };

  // İşçilik CRUD
  const iscilikEkle = async () => {
    if (!iscilikForm.isKalemi.trim() || !iscilikForm.m2BirimFiyati) return;
    const data = {
      isKalemi: iscilikForm.isKalemi.trim(),
      m2BirimFiyati: safeNum(iscilikForm.m2BirimFiyati),
      guncellemeTarihi: serverTimestamp()
    };
    if (duzenleIscilikId) {
      await updateDoc(doc(db, 'iscilikFiyatlari', duzenleIscilikId), data);
      setDuzenleIscilikId(null);
      showToast('İşçilik güncellendi');
    } else {
      await addDoc(collection(db, 'iscilikFiyatlari'), data);
      showToast('İşçilik fiyatı eklendi');
    }
    setIscilikForm({ isKalemi: '', m2BirimFiyati: '' });
  };

  const iscilikDuzenleAc = (i) => {
    setDuzenleIscilikId(i.id);
    setIscilikForm({ isKalemi: i.isKalemi, m2BirimFiyati: String(i.m2BirimFiyati) });
  };

  // Görev CRUD
  const gorevEkle = async () => {
    if (!gorevBaslik.trim()) return;
    await addDoc(collection(db, 'araclarGorevler'), {
      baslik: gorevBaslik.trim(), tamamlandi: false, olusturmaTarihi: serverTimestamp()
    });
    setGorevBaslik('');
  };

  const gorevToggle = async (id, tamamlandi) => {
    await updateDoc(doc(db, 'araclarGorevler', id), { tamamlandi: !tamamlandi });
  };

  const gorevSil = async (id) => {
    await deleteDoc(doc(db, 'araclarGorevler', id));
  };

  const hizliNotKaydet = async () => {
    await setDoc(doc(db, 'araclarNotlar', 'hizliNot'), { icerik: hizliNot });
    showToast('Not kaydedildi');
  };

  // Teklif Taslağı — İş Kalemi CRUD
  const ttIsKalemiEkle = async () => {
    const { ad, malzemeId, alanM2 } = ttYeniIsKalemi;
    if (!ad.trim() || !malzemeId || !alanM2) return;
    await addDoc(collection(db, 'ttIsKalemleri'), {
      ...ttYeniIsKalemi,
      alanM2: safeNum(alanM2),
      isciSayisi: ttYeniIsKalemi.isciSayisi !== '' ? safeNum(ttYeniIsKalemi.isciSayisi) : null,
      ozelKarOrani: ttYeniIsKalemi.ozelKarOrani !== '' ? safeNum(ttYeniIsKalemi.ozelKarOrani) : null,
      iscilikSuresi100m2: safeNum(ttYeniIsKalemi.iscilikSuresi100m2, 1),
      olusturmaTarihi: serverTimestamp()
    });
    setTtYeniIsKalemi(BOSH_IS_KALEMI);
    showToast('İş kalemi eklendi');
  };

  const ttIsKalemiSil = async (id) => {
    await deleteDoc(doc(db, 'ttIsKalemleri', id));
    showToast('İş kalemi silindi');
  };

  // FİX: ttIsKalemiDuzenleAc — TÜM alanları doldurur
  const ttIsKalemiDuzenleAc = (k) => {
    setTtDuzenleIsKalemiId(k.id);
    setTtYeniIsKalemi({
      ad: k.ad || '',
      kapsamMetni: k.kapsamMetni || '',
      malzemeId: k.malzemeId || '',
      iscilikId: k.iscilikId || '',
      alanM2: String(k.alanM2 || ''),
      isciSayisi: k.isciSayisi != null ? String(k.isciSayisi) : '',
      ozelKarOrani: k.ozelKarOrani != null ? String(k.ozelKarOrani) : '',
      iscilikSuresi100m2: String(k.iscilikSuresi100m2 || '1'),
      iscilikSuresiBirim: k.iscilikSuresiBirim || 'gun'
    });
  };

  const ttIsKalemiDuzenleKaydet = async () => {
    const { ad, malzemeId, alanM2 } = ttYeniIsKalemi;
    if (!ad.trim() || !malzemeId || !alanM2) return;
    await updateDoc(doc(db, 'ttIsKalemleri', ttDuzenleIsKalemiId), {
      ...ttYeniIsKalemi,
      alanM2: safeNum(alanM2),
      isciSayisi: ttYeniIsKalemi.isciSayisi !== '' ? safeNum(ttYeniIsKalemi.isciSayisi) : null,
      ozelKarOrani: ttYeniIsKalemi.ozelKarOrani !== '' ? safeNum(ttYeniIsKalemi.ozelKarOrani) : null,
      iscilikSuresi100m2: safeNum(ttYeniIsKalemi.iscilikSuresi100m2, 1),
    });
    setTtDuzenleIsKalemiId(null);
    setTtYeniIsKalemi(BOSH_IS_KALEMI);
    showToast('İş kalemi güncellendi');
  };

  const ttTeklifeAktar = () => {
    const gecerliKalemler = ttHesaplanmisKalemler.filter(k => !k.hata);
    if (!gecerliKalemler.length) { showToast('Aktarılacak geçerli kalem yok', 'error'); return; }
    setFiyatSatirlari(gecerliKalemler.map((k, i) => ({
      id: uid(), tanim: k.ad, miktar: String(k.alanM2 || 1),
      birim: 'm²', birimFiyat: k.birimFiyatKali.toFixed(2),
      toplam: k.toplamKali.toFixed(2), toplamModu: false
    })));
    const kapsamlar = gecerliKalemler.filter(k => k.kapsamMetni)
      .map(k => ({ id: uid(), metin: k.kapsamMetni }));
    setManuelKapsam(p => [...p, ...kapsamlar]);
    setAktifSekme('teklif');
    showToast(`${gecerliKalemler.length} kalem teklife aktarıldı!`);
  };

  // ─── RENDER: SEKME İÇERİĞİ ───

  const renderTeklif = () => {
    const seciliMetinler = seciliKapsam
      .map(id => kapsamKutuphanesi.find(x => x.id === id))
      .filter(Boolean);

    return (
      <div className="space-y-4">
        {/* Üst Bar */}
        <div className="flex items-center justify-between">
          <div />
          <div className="flex gap-2">
            <Btn variant="ghost" size="sm" onClick={() => setOnizlemeModu(p => !p)}>
              {onizlemeModu ? <><Edit3 size={12}/> Düzenle</> : <><FileText size={12}/> Önizle</>}
            </Btn>
            <Btn
              size="sm"
              onClick={teklifiKopyala}
              className={kopyaDurum === 'ok' ? '!bg-green-500 !text-white' : ''}
            >
              {kopyaDurum === 'ok' ? <><Check size={12}/> Kopyalandı!</> : <><Copy size={12}/> Kopyala</>}
            </Btn>
          </div>
        </div>

        {onizlemeModu ? (
          /* ── ÖNİZLEME ── */
          <div className="bg-white rounded-2xl p-6 text-stone-900 text-sm" style={{ fontFamily: 'Arial, sans-serif' }}>
            <div className="bg-stone-900 text-white p-4 rounded-lg mb-4 flex justify-between items-end">
              <div>
                <div className="text-2xl font-bold">AGE</div>
                <div className="text-[9px] text-stone-400 tracking-widest">İZOLASYON VE ÇATI SİSTEMLERİ</div>
              </div>
              <div className="text-[9px] text-stone-400 tracking-widest">TEKLİF FORMU</div>
            </div>
            <div className="grid grid-cols-2 gap-2 bg-stone-100 p-3 rounded-lg mb-4 text-xs">
              <div><div className="text-stone-400 text-[8px] uppercase">Tarih</div><div className="font-bold">{tarihFormatla(teklifTarihi)}</div></div>
              <div className="text-right"><div className="text-stone-400 text-[8px] uppercase">Geçerlilik</div><div className="font-bold">{gecerlilik}</div></div>
            </div>
            <div className="border-l-2 border-stone-900 pl-3 mb-4">
              <div className="text-[8px] text-stone-400 uppercase mb-1">İş Tanımı</div>
              <div className="font-bold uppercase text-sm">{isTanimi || 'Belirtilmemiş'}</div>
            </div>
            {[...seciliMetinler.map(k=>k.metin), ...manuelKapsam.map(k=>k.metin)].filter(Boolean).length > 0 && (
              <div className="mb-4">
                <div className="text-[8px] text-stone-400 uppercase mb-2">Kapsam</div>
                {[...seciliMetinler.map(k=>k.metin), ...manuelKapsam.map(k=>k.metin)].filter(Boolean).map((m,i) => (
                  <div key={i} className="text-xs text-stone-600 py-1 border-b border-stone-100">— {m}</div>
                ))}
              </div>
            )}
            <div className="mb-4">
              <div className="text-[8px] text-stone-400 uppercase mb-2">Fiyat Tablosu</div>
              <table className="w-full text-xs">
                <thead><tr className="bg-stone-900 text-white">
                  <th className="p-2 text-left rounded-l">#</th>
                  <th className="p-2 text-left">Tanım</th>
                  <th className="p-2 text-right">Miktar</th>
                  <th className="p-2 text-right">Birim F.</th>
                  <th className="p-2 text-right rounded-r">Toplam</th>
                </tr></thead>
                <tbody>
                  {fiyatSatirlari.filter(s => s.tanim.trim()).map((s, i) => (
                    <tr key={s.id} className="border-b border-stone-100">
                      <td className="p-2 text-stone-400">{i+1}</td>
                      <td className="p-2">{s.tanim}</td>
                      <td className="p-2 text-right">{s.miktar} {s.birim}</td>
                      <td className="p-2 text-right">{s.birimFiyat ? paraCevir(s.birimFiyat) : '—'}</td>
                      <td className="p-2 text-right font-bold">{paraCevir(s.toplam)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="bg-stone-900 text-white">
                  <td colSpan={4} className="p-2 font-bold rounded-l">GENEL TOPLAM</td>
                  <td className="p-2 text-right font-bold text-base rounded-r">{paraCevir(genelToplam)}</td>
                </tr></tfoot>
              </table>
            </div>
            <div className="text-[8px] text-stone-400 uppercase mb-1">Notlar</div>
            {notlar.split('\n').filter(n=>n.trim()).map((n,i) => (
              <div key={i} className="text-[10px] text-stone-500 py-0.5">— {n}</div>
            ))}
          </div>
        ) : (
          /* ── DÜZENLEME ── */
          <div className="space-y-4">
            {/* Tarih & İş Tanımı */}
            <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5 space-y-4">
              <KartBaslik icon={FileText}>Teklif Bilgileri</KartBaslik>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Etiket>Teklif Tarihi</Etiket>
                  <Alan type="date" value={teklifTarihi} onChange={e => setTeklifTarihi(e.target.value)} />
                </div>
                <div>
                  <Etiket>Geçerlilik (otomatik)</Etiket>
                  <div className="bg-stone-950 border border-stone-800 rounded-xl px-4 py-3 text-sm text-stone-400">{gecerlilik}</div>
                </div>
                <div>
                  <Etiket>İş Tanımı</Etiket>
                  <Alan
                    value={isTanimi}
                    onChange={e => setIsTanimi(e.target.value)}
                    placeholder="Çatı izolasyon uygulaması..."
                  />
                </div>
              </div>
            </div>

            {/* Kapsam */}
            <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5 space-y-4">
              <KartBaslik icon={ListChecks} right={
                <div className="flex gap-2">
                  <Btn variant="ghost" size="sm" onClick={manuelKapsamEkle}><Plus size={12}/> Manuel</Btn>
                  <Btn variant="ghost" size="sm" onClick={() => setKutuphaneModal(true)}><Settings size={12}/> Kütüphane</Btn>
                </div>
              }>Kapsam</KartBaslik>

              {/* Arama */}
              <div className="relative">
                <Search size={13} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-600" />
                <Alan
                  ref={aramaRef}
                  className="!pl-9"
                  placeholder="Kütüphaneden kapsam ara..."
                  value={kapsamArama}
                  onChange={e => setKapsamArama(e.target.value)}
                />
                {filtreliKapsam.length > 0 && (
                  <div className="absolute z-20 top-full mt-1 w-full bg-stone-900 border border-stone-700 rounded-xl shadow-2xl overflow-hidden">
                    {filtreliKapsam.map(k => (
                      <button key={k.id} onClick={() => kapsamToggle(k.id)}
                        className="w-full text-left px-4 py-3 text-sm text-stone-300 hover:bg-stone-800 transition-colors border-b border-stone-800/50 last:border-0">
                        {k.metin}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Seçili kapsam */}
              {seciliMetinler.length > 0 && (
                <div className="space-y-2">
                  {seciliMetinler.map((k, i) => (
                    <div key={k.id} className="flex items-center gap-2 bg-stone-950 border border-stone-800 rounded-xl p-3">
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => kapsamSiraYukari(i)} disabled={i === 0} className="text-stone-600 hover:text-white disabled:opacity-30 transition-colors"><ChevronUp size={12}/></button>
                        <button onClick={() => kapsamSiraAsagi(i)} disabled={i === seciliKapsam.length-1} className="text-stone-600 hover:text-white disabled:opacity-30 transition-colors"><ChevronDown size={12}/></button>
                      </div>
                      <span className="flex-1 text-sm text-stone-300">{k.metin}</span>
                      <button onClick={() => kapsamToggle(k.id)} className="text-stone-600 hover:text-red-400 transition-colors"><X size={14}/></button>
                    </div>
                  ))}
                </div>
              )}

              {/* Manuel kapsam */}
              {manuelKapsam.map(k => (
                <div key={k.id} className="flex gap-2">
                  <Alan
                    value={k.metin}
                    onChange={e => manuelKapsamGuncelle(k.id, e.target.value)}
                    placeholder="Manuel kapsam maddesi..."
                  />
                  <button onClick={() => manuelKapsamSil(k.id)} className="text-stone-600 hover:text-red-400 transition-colors px-2"><X size={14}/></button>
                </div>
              ))}
            </div>

            {/* Fiyat satırları */}
            <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5 space-y-3">
              <KartBaslik icon={DollarSign} right={
                <div className="flex items-center gap-2">
                  <span className="text-sm font-black text-white">{paraCevir(genelToplam)}</span>
                  <Btn variant="ghost" size="sm" onClick={fiyatSatirEkle}><Plus size={12}/> Satır</Btn>
                </div>
              }>Fiyat Tablosu</KartBaslik>

              {fiyatSatirlari.map((s, i) => (
                <div key={s.id} className="bg-stone-950 border border-stone-800 rounded-xl p-3 space-y-2">
                  <div className="flex gap-2">
                    <span className="text-[10px] text-stone-600 font-black pt-3.5 w-5 shrink-0">{i+1}</span>
                    <Alan
                      className="flex-1"
                      value={s.tanim}
                      onChange={e => fiyatSatirGuncelle(s.id, 'tanim', e.target.value)}
                      placeholder="İş kalemi tanımı..."
                    />
                    <button
                      onClick={() => toplamModuToggle(s.id)}
                      title={s.toplamModu ? 'Formüle dön' : 'Direkt toplam gir'}
                      className={`px-3 rounded-xl border text-[9px] font-black uppercase shrink-0 transition-colors ${
                        s.toplamModu ? 'border-amber-500/40 bg-amber-500/10 text-amber-400' : 'border-stone-800 text-stone-600 hover:text-stone-400'
                      }`}
                    >
                      {s.toplamModu ? 'Σ' : '×'}
                    </button>
                    <button
                      onClick={() => fiyatSatirSil(s.id)}
                      disabled={fiyatSatirlari.length <= 1}
                      className="text-stone-700 hover:text-red-400 transition-colors disabled:opacity-30 px-1"
                    ><X size={14}/></button>
                  </div>
                  <div className="flex gap-2 pl-7">
                    <Alan className="w-20" type="number" value={s.miktar} onChange={e => fiyatSatirGuncelle(s.id,'miktar',e.target.value)} placeholder="Miktar" disabled={s.toplamModu} />
                    <Secim className="w-24" value={s.birim} onChange={e => fiyatSatirGuncelle(s.id,'birim',e.target.value)}>
                      {['m²','m','adet','kg','lt','takım','iş'].map(b => <option key={b}>{b}</option>)}
                    </Secim>
                    {!s.toplamModu ? (
                      <Alan className="flex-1" type="number" value={s.birimFiyat} onChange={e => fiyatSatirGuncelle(s.id,'birimFiyat',e.target.value)} placeholder="Birim fiyat (₺)" />
                    ) : (
                      <Alan className="flex-1" type="number" value={s.toplam} onChange={e => fiyatSatirGuncelle(s.id,'toplam',e.target.value)} placeholder="Toplam (₺)" />
                    )}
                    {!s.toplamModu && (
                      <div className="w-28 bg-stone-900 border border-stone-800 rounded-xl px-3 py-3 text-right text-sm font-black text-white">
                        {s.toplam ? paraCevir(s.toplam) : '—'}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="flex justify-end">
                <div className="bg-stone-950 border border-stone-700 rounded-xl px-6 py-3 flex items-center gap-3">
                  <span className="text-[10px] font-black text-stone-500 uppercase tracking-widest">Genel Toplam</span>
                  <span className="text-xl font-black text-white">{paraCevir(genelToplam)}</span>
                </div>
              </div>
            </div>

            {/* Notlar */}
            <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5">
              <KartBaslik icon={StickyNote} right={
                <Btn variant="ghost" size="sm" onClick={() => setNotlar(VARSAYILAN_NOTLAR.join('\n'))}>
                  <RefreshCw size={11}/> Sıfırla
                </Btn>
              }>Notlar & Şartlar</KartBaslik>
              <textarea
                value={notlar}
                onChange={e => setNotlar(e.target.value)}
                rows={5}
                className="w-full bg-stone-950 border border-stone-800 rounded-xl px-4 py-3 text-sm text-stone-300 outline-none focus:border-stone-500 resize-none placeholder:text-stone-700 transition-colors"
                placeholder="Her satır bir not maddesi..."
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTaslak = () => {
    const kalemForm = ttYeniIsKalemi;
    const duzenleniyor = !!ttDuzenleIsKalemiId;

    return (
      <div className="space-y-4">
        {/* Alt Sekmeler */}
        <div className="flex gap-1 bg-stone-900/50 border border-stone-800 rounded-xl p-1">
          {[
            { id: 'kalemler', label: 'İş Kalemleri' },
            { id: 'parametreler', label: 'Parametreler' },
            { id: 'ozet', label: 'Özet & Aktar' },
          ].map(s => (
            <button key={s.id} onClick={() => setTtAltSekme(s.id)}
              className={`flex-1 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${
                ttAltSekme === s.id ? 'bg-stone-800 text-white' : 'text-stone-500 hover:text-stone-300'
              }`}>
              {s.label}
            </button>
          ))}
        </div>

        {ttAltSekme === 'parametreler' && (
          <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5 space-y-4">
            <KartBaslik icon={Settings}>Global Hesaplama Parametreleri</KartBaslik>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Etiket>Varsayılan İşçi Sayısı</Etiket>
                <Alan type="number" value={ttVarsayilanIsci} onChange={e => setTtVarsayilanIsci(safeNum(e.target.value,4))} min={1} />
              </div>
              <div>
                <Etiket>Yevmiye (₺/gün)</Etiket>
                <Alan type="number" value={ttVarsayilanYevmiye} onChange={e => setTtVarsayilanYevmiye(safeNum(e.target.value,5000))} />
              </div>
              <div>
                <Etiket>Kar Oranı (%)</Etiket>
                <Alan type="number" value={ttVarsayilanKar} onChange={e => setTtVarsayilanKar(safeNum(e.target.value,15))} min={0} max={100} />
              </div>
            </div>
            <div className="bg-stone-950 border border-stone-800 rounded-xl p-4 text-xs text-stone-500 space-y-1">
              <p className="text-stone-400 font-black mb-2 uppercase text-[10px] tracking-wider">ℹ Hesaplama Mantığı</p>
              <p>• İşçilik ID seçiliyse: Alan × m²BirimFiyat (Fiyat Takibi'nden)</p>
              <p>• İşçilik ID seçilmemişse: (Alan / 100) × İşçilikSüresi × Yevmiye × İşçi</p>
              <p>• Malzeme: Alan × Sarfiyat × BirimFiyat (Fiyat Takibi → Son Fiyatlar)</p>
            </div>
          </div>
        )}

        {ttAltSekme === 'kalemler' && (
          <div className="space-y-4">
            {/* İş Kalemi Formu */}
            <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5 space-y-4">
              <KartBaslik icon={Plus}>{duzenleniyor ? 'Kalemi Düzenle' : 'Yeni İş Kalemi'}</KartBaslik>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Etiket>İş Kalemi Adı *</Etiket>
                  <Alan value={kalemForm.ad} onChange={e => setTtYeniIsKalemi(p=>({...p, ad: e.target.value}))} placeholder="Çatı izolasyon uygulaması..." />
                </div>
                <div>
                  <Etiket>Kapsam Metni</Etiket>
                  <Alan value={kalemForm.kapsamMetni} onChange={e => setTtYeniIsKalemi(p=>({...p, kapsamMetni: e.target.value}))} placeholder="Teklife aktarılacak kapsam maddesi..." />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Etiket>Malzeme * (Son Fiyatlar)</Etiket>
                  <Secim value={kalemForm.malzemeId} onChange={e => setTtYeniIsKalemi(p=>({...p, malzemeId: e.target.value}))}>
                    <option value="">— Seçin —</option>
                    {sonFiyatlar.map(m => (
                      <option key={m.id} value={m.id}>{m.malzemeAdi} — {paraCevir(m.fiyat)}/{m.birim}</option>
                    ))}
                  </Secim>
                </div>
                <div>
                  <Etiket>İşçilik Fiyatı</Etiket>
                  <Secim value={kalemForm.iscilikId} onChange={e => setTtYeniIsKalemi(p=>({...p, iscilikId: e.target.value}))}>
                    <option value="">Yevmiye bazlı</option>
                    {iscilikFiyatlari.map(i => (
                      <option key={i.id} value={i.id}>{i.isKalemi} — {paraCevir(i.m2BirimFiyati)}/m²</option>
                    ))}
                  </Secim>
                </div>
                <div>
                  <Etiket>Alan (m²) *</Etiket>
                  <Alan type="number" value={kalemForm.alanM2} onChange={e => setTtYeniIsKalemi(p=>({...p, alanM2: e.target.value}))} placeholder="250" />
                </div>
                <div>
                  <Etiket>Özel Kar (% — boş=varsayılan)</Etiket>
                  <Alan type="number" value={kalemForm.ozelKarOrani} onChange={e => setTtYeniIsKalemi(p=>({...p, ozelKarOrani: e.target.value}))} placeholder={`${ttVarsayilanKar}%`} />
                </div>
              </div>
              {/* Sadece iscilikId seçilmemişse fallback alanları göster */}
              {!kalemForm.iscilikId && (
                <div className="grid grid-cols-2 gap-3 border-t border-stone-800 pt-3">
                  <div>
                    <Etiket>İşçilik Süresi (100m² için — gün)</Etiket>
                    <Alan type="number" value={kalemForm.iscilikSuresi100m2} onChange={e => setTtYeniIsKalemi(p=>({...p, iscilikSuresi100m2: e.target.value}))} placeholder="1" />
                  </div>
                  <div>
                    <Etiket>İşçi Sayısı (boş=varsayılan)</Etiket>
                    <Alan type="number" value={kalemForm.isciSayisi} onChange={e => setTtYeniIsKalemi(p=>({...p, isciSayisi: e.target.value}))} placeholder={String(ttVarsayilanIsci)} />
                  </div>
                </div>
              )}
              <div className="flex gap-2 pt-1">
                {duzenleniyor && (
                  <Btn variant="ghost" onClick={() => { setTtDuzenleIsKalemiId(null); setTtYeniIsKalemi(BOSH_IS_KALEMI); }}>
                    <X size={12}/> İptal
                  </Btn>
                )}
                <Btn onClick={duzenleniyor ? ttIsKalemiDuzenleKaydet : ttIsKalemiEkle} className="flex-1 justify-center">
                  {duzenleniyor ? <><Save size={14}/> Kaydet</> : <><Plus size={14}/> Ekle</>}
                </Btn>
              </div>
            </div>

            {/* İş kalemleri listesi */}
            {ttIsKalemleri.length > 0 && (
              <div className="space-y-2">
                {ttHesaplanmisKalemler.map(k => (
                  <div key={k.id} className={`bg-stone-900 border rounded-2xl p-4 ${k.hata ? 'border-red-500/30' : 'border-stone-800'}`}>
                    {k.hata ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-red-400"><AlertCircle size={14}/><span className="text-sm font-bold">{k.ad}</span><span className="text-xs">— {k.hata}</span></div>
                        <div className="flex gap-2">
                          <Btn variant="ghost" size="sm" onClick={() => ttIsKalemiDuzenleAc(k)}><Edit3 size={12}/></Btn>
                          <Btn variant="danger" size="sm" onClick={() => ttIsKalemiSil(k.id)}><Trash2 size={12}/></Btn>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="font-black text-white text-sm">{k.ad}</div>
                            <div className="text-[10px] text-stone-500 mt-0.5">{k.malzemeAdi} · {k.alanM2} m² · %{k.karOrani} kar</div>
                          </div>
                          <div className="flex gap-2">
                            <Btn variant="ghost" size="sm" onClick={() => ttIsKalemiDuzenleAc(k)}><Edit3 size={12}/></Btn>
                            <Btn variant="danger" size="sm" onClick={() => ttIsKalemiSil(k.id)}><Trash2 size={12}/></Btn>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {[
                            { l: 'Malzeme', v: paraCevir(k.malzemeMaliyeti), c: 'text-stone-300' },
                            { l: 'İşçilik', v: paraCevir(k.iscilikMaliyeti), c: 'text-blue-400' },
                            { l: 'Kar (+%'+k.karOrani+')', v: paraCevir(k.toplamKali - k.araToplam), c: 'text-purple-400' },
                            { l: 'Birim Fiyat', v: paraCevir(k.birimFiyatKali)+'/m²', c: 'text-green-400' },
                          ].map(x => (
                            <div key={x.l} className="bg-stone-950 rounded-xl p-2.5 text-center">
                              <div className="text-[8px] text-stone-600 uppercase font-black">{x.l}</div>
                              <div className={`text-xs font-black mt-1 ${x.c}`}>{x.v}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {ttAltSekme === 'ozet' && (
          <div className="space-y-4">
            {ttHesaplanmisKalemler.filter(k => !k.hata).length > 0 ? (
              <>
                <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5">
                  <KartBaslik icon={Calculator}>Özet</KartBaslik>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-stone-950 border border-stone-800 rounded-xl p-4 text-center">
                      <div className="text-[9px] text-stone-600 uppercase font-black">Toplam Alan</div>
                      <div className="text-lg font-black text-white mt-1">{ttHesaplanmisKalemler.reduce((a,k)=>a+safeNum(k.alanM2),0)} m²</div>
                    </div>
                    <div className="bg-stone-950 border border-stone-800 rounded-xl p-4 text-center">
                      <div className="text-[9px] text-stone-600 uppercase font-black">Kalem Sayısı</div>
                      <div className="text-lg font-black text-white mt-1">{ttHesaplanmisKalemler.filter(k=>!k.hata).length}</div>
                    </div>
                    <div className="bg-stone-950 border border-stone-700 rounded-xl p-4 text-center">
                      <div className="text-[9px] text-stone-500 uppercase font-black">Genel Toplam</div>
                      <div className="text-lg font-black text-green-400 mt-1">{paraCevir(ttGenelToplam)}</div>
                    </div>
                  </div>
                </div>
                <Btn onClick={ttTeklifeAktar} className="w-full justify-center !py-5 !text-sm !rounded-2xl">
                  <ArrowRight size={18}/> Teklif Sihirbazına Aktar
                </Btn>
              </>
            ) : (
              <div className="text-center py-12 text-stone-600">
                <Layers size={32} className="mx-auto mb-3 opacity-30" />
                <p className="font-black text-sm">Geçerli iş kalemi yok</p>
                <p className="text-xs mt-1 text-stone-700">İş Kalemleri sekmesinden ekleyin</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderHesap = () => (
    <div className="space-y-4">
      <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5 space-y-4">
        <KartBaslik icon={Calculator}>Alan Girişi</KartBaslik>
        <div>
          <Etiket>Uygulama Alanı (m²)</Etiket>
          <Alan type="number" value={m2Alani} onChange={e => setM2Alani(e.target.value)} placeholder="250" className="text-lg" />
        </div>
      </div>

      {sarfiyatSonuclari.length > 0 && (
        <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5 space-y-3">
          <KartBaslik icon={Package}>Hesaplanan Sarfiyat</KartBaslik>
          {sarfiyatSonuclari.map((s, i) => (
            <div key={i} className="flex items-center justify-between bg-stone-950 border border-stone-800 rounded-xl p-4">
              <div>
                <div className="text-sm font-black text-white">{s.urun}</div>
                <div className="text-[10px] text-stone-600 mt-0.5">{s.aciklama}</div>
              </div>
              <div className="text-right">
                <div className="text-xl font-black text-white">{s.miktar}</div>
                <div className="text-[10px] text-stone-500 uppercase font-black">{s.birim}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Serbest Sarfiyat */}
      <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5 space-y-4">
        <KartBaslik icon={Zap}>Serbest Sarfiyat Hesabı</KartBaslik>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Etiket>Malzeme Adı</Etiket>
            <Alan value={serbestAd} onChange={e => setSerbestAd(e.target.value)} placeholder="Malzeme adı" />
          </div>
          <div>
            <Etiket>Alan (m²)</Etiket>
            <Alan type="number" value={serbestM2} onChange={e => setSerbestM2(e.target.value)} placeholder="250" />
          </div>
          <div>
            <Etiket>Oran (birim/m²)</Etiket>
            <Alan type="number" value={serbestOran} onChange={e => setSerbestOran(e.target.value)} placeholder="2.5" />
          </div>
          <div>
            <Etiket>Birim</Etiket>
            <Secim value={serbestBirim} onChange={e => setSerbestBirim(e.target.value)}>
              {['kg','lt','m²','adet','m','ton'].map(b => <option key={b}>{b}</option>)}
            </Secim>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFiyat = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Malzeme Fiyatları */}
        <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5 space-y-4">
          <KartBaslik icon={Package}>
            {duzenleMalzemeId ? 'Malzeme Düzenle' : 'Malzeme Fiyatı Ekle'}
          </KartBaslik>
          <div className="space-y-3">
            <div>
              <Etiket>Malzeme Adı</Etiket>
              <Alan value={malzemeForm.ad} onChange={e => setMalzemeForm(p=>({...p, ad: e.target.value}))} placeholder="XPS Levha..." />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Etiket>Fiyat (₺)</Etiket>
                <Alan type="number" value={malzemeForm.fiyat} onChange={e => setMalzemeForm(p=>({...p, fiyat: e.target.value}))} placeholder="450" />
              </div>
              <div>
                <Etiket>Birim</Etiket>
                <Secim value={malzemeForm.birim} onChange={e => setMalzemeForm(p=>({...p, birim: e.target.value}))}>
                  {['m²','kg','lt','adet','m','ton'].map(b => <option key={b}>{b}</option>)}
                </Secim>
              </div>
            </div>
            <div>
              <Etiket>Sarfiyat (birim/m²) — Taslak hesaplamada kullanılır</Etiket>
              <Alan type="number" value={malzemeForm.sarfiyat} onChange={e => setMalzemeForm(p=>({...p, sarfiyat: e.target.value}))} placeholder="1.05" />
            </div>
            <div className="flex gap-2">
              {duzenleMalzemeId && (
                <Btn variant="ghost" onClick={() => { setDuzenleMalzemeId(null); setMalzemeForm({ad:'',fiyat:'',birim:'m²',sarfiyat:'1'}); }}>
                  <X size={12}/> İptal
                </Btn>
              )}
              <Btn onClick={malzemeEkle} className="flex-1 justify-center">
                {duzenleMalzemeId ? <><Save size={14}/> Güncelle</> : <><Plus size={14}/> Ekle</>}
              </Btn>
            </div>
          </div>
        </div>

        {/* İşçilik Fiyatları */}
        <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5 space-y-4">
          <KartBaslik icon={Briefcase}>
            {duzenleIscilikId ? 'İşçilik Düzenle' : 'İşçilik Fiyatı Ekle'}
          </KartBaslik>
          <div className="space-y-3">
            <div>
              <Etiket>İş Kalemi</Etiket>
              <Alan value={iscilikForm.isKalemi} onChange={e => setIscilikForm(p=>({...p, isKalemi: e.target.value}))} placeholder="Membran uygulaması..." />
            </div>
            <div>
              <Etiket>m² Birim Fiyat (₺/m²)</Etiket>
              <Alan type="number" value={iscilikForm.m2BirimFiyati} onChange={e => setIscilikForm(p=>({...p, m2BirimFiyati: e.target.value}))} placeholder="150" />
            </div>
            <div className="flex gap-2">
              {duzenleIscilikId && (
                <Btn variant="ghost" onClick={() => { setDuzenleIscilikId(null); setIscilikForm({isKalemi:'',m2BirimFiyati:''}); }}>
                  <X size={12}/> İptal
                </Btn>
              )}
              <Btn onClick={iscilikEkle} className="flex-1 justify-center">
                {duzenleIscilikId ? <><Save size={14}/> Güncelle</> : <><Plus size={14}/> Ekle</>}
              </Btn>
            </div>
          </div>
        </div>
      </div>

      {/* Son Fiyatlar */}
      {sonFiyatlar.length > 0 && (
        <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5">
          <KartBaslik icon={TrendingUp}>Son Malzeme Fiyatları</KartBaslik>
          <div className="space-y-2">
            {sonFiyatlar.map(m => (
              <div key={m.id} className="flex items-center justify-between bg-stone-950 border border-stone-800 rounded-xl p-3 group">
                <div>
                  <div className="text-sm font-black text-white">{m.malzemeAdi}</div>
                  <div className="text-[10px] text-stone-600">Sarfiyat: {m.sarfiyat || '—'} {m.birim}/m²</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-sm font-black text-green-400">{paraCevir(m.fiyat)}</div>
                    <div className="text-[9px] text-stone-600">/{m.birim}</div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Btn variant="ghost" size="sm" onClick={() => malzemeDuzenleAc(m)}><Edit3 size={11}/></Btn>
                    <Btn variant="danger" size="sm" onClick={() => malzemeSil(m.id)}><Trash2 size={11}/></Btn>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* İşçilik Listesi */}
      {iscilikFiyatlari.length > 0 && (
        <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5">
          <KartBaslik icon={Briefcase}>İşçilik Birim Fiyatları</KartBaslik>
          <div className="space-y-2">
            {iscilikFiyatlari.map(i => (
              <div key={i.id} className="flex items-center justify-between bg-stone-950 border border-stone-800 rounded-xl p-3 group">
                <span className="text-sm font-black text-white">{i.isKalemi}</span>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-sm font-black text-blue-400">{paraCevir(i.m2BirimFiyati)}</div>
                    <div className="text-[9px] text-stone-600">/m²</div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Btn variant="ghost" size="sm" onClick={() => iscilikDuzenleAc(i)}><Edit3 size={11}/></Btn>
                    <Btn variant="danger" size="sm" onClick={async () => { await deleteDoc(doc(db,'iscilikFiyatlari',i.id)); showToast('Silindi'); }}><Trash2 size={11}/></Btn>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderGorev = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Görevler */}
        <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5 space-y-4">
          <KartBaslik icon={CheckSquare}>Görevler</KartBaslik>
          <div className="flex gap-2">
            <Alan
              value={gorevBaslik}
              onChange={e => setGorevBaslik(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && gorevEkle()}
              placeholder="Yeni görev..."
            />
            <Btn onClick={gorevEkle} size="sm" disabled={!gorevBaslik.trim()}><Plus size={13}/></Btn>
          </div>
          <div className="space-y-2">
            {gorevler.length === 0 && (
              <p className="text-stone-700 text-xs text-center py-6 font-black uppercase tracking-widest">Görev yok</p>
            )}
            {gorevler.map(g => (
              <div key={g.id} className="flex items-center gap-3 bg-stone-950 border border-stone-800 rounded-xl p-3 group">
                <button onClick={() => gorevToggle(g.id, g.tamamlandi)}
                  className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all shrink-0 ${
                    g.tamamlandi ? 'border-green-500 bg-green-500/20' : 'border-stone-700 hover:border-stone-500'
                  }`}>
                  {g.tamamlandi && <Check size={11} className="text-green-400" />}
                </button>
                <span className={`flex-1 text-sm ${g.tamamlandi ? 'line-through text-stone-600' : 'text-stone-300'}`}>{g.baslik}</span>
                <button onClick={() => gorevSil(g.id)} className="opacity-0 group-hover:opacity-100 text-stone-700 hover:text-red-400 transition-all"><Trash2 size={13}/></button>
              </div>
            ))}
          </div>
          {gorevler.filter(g=>g.tamamlandi).length > 0 && (
            <Btn variant="ghost" size="sm" onClick={async () => {
              for (const g of gorevler.filter(g=>g.tamamlandi)) await deleteDoc(doc(db,'araclarGorevler',g.id));
              showToast('Tamamlananlar silindi');
            }}>
              <Trash2 size={11}/> Tamamlananları Sil
            </Btn>
          )}
        </div>

        {/* Hızlı Not */}
        <div className="bg-stone-900 border border-stone-800 rounded-2xl p-5 space-y-4">
          <KartBaslik icon={StickyNote} right={
            <Btn size="sm" onClick={hizliNotKaydet}><Save size={12}/> Kaydet</Btn>
          }>Hızlı Not</KartBaslik>
          <textarea
            value={hizliNot}
            onChange={e => setHizliNot(e.target.value)}
            rows={10}
            className="w-full bg-stone-950 border border-stone-800 rounded-xl px-4 py-3 text-sm text-stone-300 outline-none focus:border-stone-500 resize-none placeholder:text-stone-700 transition-colors"
            placeholder="Notlarınızı buraya yazın..."
          />
        </div>
      </div>
    </div>
  );

  const renderSekme = () => {
    switch (aktifSekme) {
      case 'teklif':  return renderTeklif();
      case 'taslak':  return renderTaslak();
      case 'hesap':   return renderHesap();
      case 'fiyat':   return renderFiyat();
      case 'gorev':   return renderGorev();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-white pb-28">
      {/* HEADER */}
      <div className="sticky top-0 z-30 bg-stone-950/95 backdrop-blur border-b border-stone-900">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="p-2 bg-stone-900 border border-stone-800 rounded-xl">
            <Wrench size={16} className="text-stone-400" />
          </div>
          <div>
            <h1 className="text-sm font-black text-white tracking-tight">Araçlar</h1>
            <p className="text-[9px] text-stone-600 font-black uppercase tracking-widest">Teklif · Hesap · Takip</p>
          </div>
        </div>
      </div>

      {/* SEKMELER */}
      <div className="max-w-5xl mx-auto px-4 mt-4">
        <div className="flex gap-1 bg-stone-900/60 border border-stone-800/60 p-1 rounded-2xl overflow-x-auto no-scrollbar">
          {SEKMELER.map(s => (
            <button
              key={s.id}
              onClick={() => { setAktifSekme(s.id); setOnizlemeModu(false); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all whitespace-nowrap ${
                aktifSekme === s.id
                  ? 'bg-stone-800 text-white shadow'
                  : 'text-stone-600 hover:text-stone-300'
              }`}
            >
              <s.icon size={12} />
              <span className="hidden sm:inline">{s.label}</span>
              <span className="sm:hidden">{s.kisa}</span>
            </button>
          ))}
        </div>
      </div>

      {/* İÇERİK */}
      <div className="max-w-5xl mx-auto px-4 py-5">
        {renderSekme()}
      </div>

      {/* KÜTÜPHANE MODALI */}
      {kutuphaneModal && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setKutuphaneModal(false)}>
          <div className="bg-stone-900 border border-stone-800 rounded-[2rem] w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-stone-800">
              <div className="flex items-center gap-2">
                <Settings size={14} className="text-stone-500" />
                <h3 className="text-sm font-black uppercase tracking-widest text-white">Kapsam Kütüphanesi</h3>
              </div>
              <button onClick={() => setKutuphaneModal(false)} className="p-2 text-stone-600 hover:text-white hover:bg-stone-800 rounded-xl transition-all"><X size={16}/></button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              <div className="flex gap-2">
                <Alan
                  value={yeniKapsamMetin}
                  onChange={e => setYeniKapsamMetin(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && kapsamKutuphanesineEkle()}
                  placeholder="Sık kullandığınız iş tanımı..."
                />
                <Btn onClick={kapsamKutuphanesineEkle} disabled={!yeniKapsamMetin.trim()}>
                  <Plus size={13}/> Ekle
                </Btn>
              </div>
              <div className="space-y-2">
                {kapsamKutuphanesi.map(k => (
                  <div key={k.id} className="flex gap-2 items-center bg-stone-950 border border-stone-800 rounded-xl p-3 group">
                    {duzenleKapsamId === k.id ? (
                      <>
                        <Alan value={duzenleKapsamMetin} onChange={e => setDuzenleKapsamMetin(e.target.value)} className="flex-1" />
                        <Btn variant="success" size="sm" onClick={kapsamDuzenleKaydet}><Check size={13}/></Btn>
                        <Btn variant="ghost" size="sm" onClick={() => setDuzenleKapsamId(null)}><X size={13}/></Btn>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm text-stone-300">{k.metin}</span>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Btn variant="ghost" size="sm" onClick={() => { setDuzenleKapsamId(k.id); setDuzenleKapsamMetin(k.metin); }}><Edit3 size={11}/></Btn>
                          <Btn variant="danger" size="sm" onClick={() => kapsamKutuphanesindenSil(k.id)}><Trash2 size={11}/></Btn>
                        </div>
                      </>
                    )}
                  </div>
                ))}
                {kapsamKutuphanesi.length === 0 && (
                  <p className="text-center text-xs text-stone-700 italic py-8 font-black uppercase tracking-widest">Kütüphane boş</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
      {toast && (
        <div className={`fixed bottom-6 right-4 z-[200] px-5 py-3.5 rounded-2xl flex items-center gap-2.5 shadow-2xl text-sm font-black tracking-wide ${
          toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-stone-900'
        }`}>
          {toast.type === 'error' ? <AlertCircle size={16}/> : <Check size={16}/>}
          {toast.msg}
        </div>
      )}
    </div>
  );
}